import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button, Modal, Card, Typography, Space, Spin, Alert } from "antd";
import QrScanner from "qr-scanner";
import axios from "../services/axiosConfig";
import Helper from "../services/Helper";
import Notification from "../services/Notification";

const { Text } = Typography;

const ScannerModalContent = ({ onScanSuccess }) => {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    if (!videoRef.current) return undefined;

    const scanner = new QrScanner(
      videoRef.current,
      (result) => {
        const value =
          typeof result === "object" && result?.data ? result.data : result;
        onScanSuccess(value);
      },
      { preferredCamera: "environment" },
    );

    scannerRef.current = scanner;

    scanner
      .start()
      .then(() => {
        if (isMounted) setLoading(false);
      })
      .catch((err) => {
        console.error("Camera start error:", err);
        if (isMounted) {
          setLoading(false);
          setError(
            "Unable to access camera. Please ensure camera permissions are allowed and your device has an active camera.",
          );
        }
      });

    return () => {
      isMounted = false;
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [onScanSuccess]);

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "300px",
          backgroundColor: "#1f1f1f",
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading && (
          <div style={{ position: "absolute", zIndex: 10 }}>
            <Spin
              size="large"
              tip="Starting camera..."
              style={{ color: "#fff" }}
            />
          </div>
        )}
        <video
          ref={videoRef}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>
      {error && (
        <Alert
          message="Camera Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: "16px", textAlign: "left" }}
        />
      )}
    </div>
  );
};

const QRScanner = ({ onScan }) => {
  const [open, setOpen] = useState(false);
  const [scannedResult, setScannedResult] = useState("");
  const onScanRef = useRef(onScan);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const handleScanSuccess = useCallback((value) => {
    setScannedResult(value);
    if (typeof onScanRef.current === "function") {
      onScanRef.current(value);
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    const checkBalance = async () => {
      if (scannedResult?.split("|")[1]) {
        const data = await axios
          .get(`/get-consumer-balance?phone=${scannedResult?.split("|")[1]}`)
          .catch((error) => {
            Helper.ErrorHanlder(error);
          });

        if (data && data.data) {
          setBalance(data.data.data ? Number(data.data.data) : "");
        }
      }
    };
    scannedResult && checkBalance();
  }, [scannedResult]);

  const handleUpdateOrderStatus = async () => {
    const data = await axios
      .patch(`/update-order-status`, { orderId: scannedResult?.split("|")[0] })
      .catch((error) => {
        Helper.ErrorHanlder(error);
      });

    if (data && data.data) {
      Notification.success("Order status updated successfully");
      setScannedResult("");
      setBalance("");
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "600px", margin: "0 auto" }}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Card title="QR Code Scanner">
          {!scannedResult && (
            <Button type="primary" onClick={() => setOpen(true)}>
              Scan QR Code
            </Button>
          )}

          {scannedResult && balance && balance > 0 ? (
            <div style={{ marginTop: "16px" }}>
              <Text type="secondary">Balance Amount:</Text>
              <div
                style={{
                  marginTop: "8px",
                  padding: "12px",
                  background: "#f5f5f5",
                  borderRadius: "6px",
                  wordBreak: "break-all",
                }}
              >
                <Text>{balance.toFixed(2)}</Text>
              </div>
            </div>
          ) : (
            scannedResult &&
            balance === 0 && (
              <Button
                type="primary"
                style={{ marginTop: "16px" }}
                onClick={() => {
                  setBalance(0);
                  handleUpdateOrderStatus();
                }}
              >
                Update Order Status
              </Button>
            )
          )}
        </Card>
      </Space>

      <Modal
        title="Scan Consumer QR"
        open={open}
        footer={null}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        {open && <ScannerModalContent onScanSuccess={handleScanSuccess} />}
      </Modal>
    </div>
  );
};

export default QRScanner;
